// src/pages/GoogleCalendarCallback.jsx
// Handles OAuth callback from Google, exchanges code for tokens via API
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import posthog from 'posthog-js';
import { Calendar, CheckCircle, XCircle } from 'lucide-react';
import styles from './GoogleCalendarCallback.module.css';
import { parseSafeRedirectUri } from '../lib/oauthConsent';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function GoogleCalendarCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Connecting your Google Calendar...');

  useEffect(() => { handleCallback(); }, []);

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) throw new Error(errorDescription || `Google authorization failed: ${error}`);
      if (!code) throw new Error('No authorization code received from Google');
      if (!state) throw new Error('No state parameter received');

      setMessage('Exchanging authorization code...');

      const response = await fetch(`${API_URL}/v1/calendars/google/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, state })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to connect Google Calendar');
      }

      const result = await response.json();

      setMessage('Syncing your calendar...');

      if (user) {
        try {
          const { data: { session } } = await import('../lib/supabase.js').then(m => m.supabase.auth.getSession());
          if (session) {
            await fetch(`${API_URL}/v1/calendars/sync?provider=google`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' }
            });
          }
        } catch (syncError) {
          console.warn('Initial sync failed:', syncError);
        }
      }

      posthog.capture('calendar_connect', { provider: 'google' });
      setStatus('success');
      setMessage('Google Calendar connected successfully!');

      setTimeout(() => {
        // External redirect if the API returned a custom redirect_url. Only
        // https (or loopback http) — anything else (javascript:, data:) would
        // run on the portal origin, so it falls through to the dashboard.
        const safeRedirect = result.redirect_url ? parseSafeRedirectUri(result.redirect_url) : null;
        if (safeRedirect) {
          safeRedirect.searchParams.set('google_connected', 'true');
          window.location.href = safeRedirect.toString();
          return;
        }

        const isVibeCoderOnboarding = localStorage.getItem('vibeCoderOnboarding') === 'true';
        if (isVibeCoderOnboarding) {
          localStorage.removeItem('vibeCoderOnboarding');
          navigate('/get-started?google_connected=true');
        } else {
          navigate('/dashboard?google_connected=true');
        }
      }, 2000);

    } catch (error) {
      console.error('Google Calendar callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to connect Google Calendar');

      setTimeout(() => {
        const isVibeCoderOnboarding = localStorage.getItem('vibeCoderOnboarding') === 'true';
        if (isVibeCoderOnboarding) {
          localStorage.removeItem('vibeCoderOnboarding');
          navigate('/get-started?google_error=' + encodeURIComponent(error.message));
        } else {
          navigate('/dashboard?google_error=' + encodeURIComponent(error.message));
        }
      }, 4000);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.iconWrap}>
          {status === 'processing' && <div className={styles.spinner} aria-label="Loading" />}
          {status === 'success' && <CheckCircle className={styles.iconSuccess} size={64} />}
          {status === 'error' && <XCircle className={styles.iconError} size={64} />}
        </div>

        <h2 className={styles.title}>
          {status === 'processing' && 'Connecting Calendar...'}
          {status === 'success' && 'Success!'}
          {status === 'error' && 'Connection Failed'}
        </h2>

        <p className={styles.message}>{message}</p>

        <div className={styles.providerRow}>
          <Calendar size={16} aria-hidden="true" />
          <span>Google Calendar</span>
        </div>

        {status === 'error' && (
          <div className={styles.troubleshootBox}>
            <p className={styles.troubleshootHeading}>Troubleshooting:</p>
            <ul className={styles.troubleshootList}>
              <li>Make sure you granted calendar permissions</li>
              <li>Check that your Google account is active</li>
              <li>Try disconnecting and reconnecting your calendar</li>
            </ul>
          </div>
        )}

        {status === 'processing' && (
          <div className={styles.dotsWrap} aria-hidden="true">
            <div className={styles.dot} style={{ animationDelay: '0ms' }} />
            <div className={styles.dot} style={{ animationDelay: '150ms' }} />
            <div className={styles.dot} style={{ animationDelay: '300ms' }} />
          </div>
        )}

        {status !== 'processing' && (
          <p className={styles.redirectNote}>Redirecting to dashboard...</p>
        )}
      </div>
    </div>
  );
}
